import { FC } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Tile,
} from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import { Divider, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  internalShopInventoryEventsFiltersSchema,
  useExportToXls,
  useInternalShopInventoryEvents,
} from 'store/internal-shop';
import { useRouteParams } from 'utils/useIdParam';
import moment from 'moment';
import { DownloadXlsAction } from 'components/actions';
import { useModalContext } from 'store/modal';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import {
  InventoryUpdateModal,
  ShopInternalDisabledFeatureAlert,
} from 'containers/shop';
import { useSearchParams } from 'utils/navigation';
import { useRouteMatch } from 'react-router-dom';

export const ShopInternalInventoryDetails: FC = () => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { id: variantId } = useRouteParams(
    ROUTES.shop.internal.inventory.details,
  );
  const { defaultLanguage } = useCompanyLanguages();
  const { openModal } = useModalContext();
  const { hasEditPermission } = useMe();
  const { searchParams: filters, navigate } = useSearchParams(
    internalShopInventoryEventsFiltersSchema,
  );

  const { data: events, isLoading } = useInternalShopInventoryEvents(
    variantId,
    filters,
  );

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <Stack gap={2}>
      <ShopInternalDisabledFeatureAlert />

      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('shop.internal.title'),
            url: ROUTES.shop.internal.items.list,
          },
          {
            label: t('shop.inventory.title'),
            url: ROUTES.shop.internal.inventory.list,
          },
          {
            label: [
              events?.meta.itemName,
              events?.meta.translations.find(
                (trans) => trans.language === defaultLanguage,
              )?.title,
            ]
              .filter(Boolean)
              .join(' > '),
          },
        ]}
        side={
          <ActionsMenu
            items={[
              {
                label: t('shop.actions.add_inventory'),
                disabled: !hasEditPermission(['internalMerch']),
                onClick: () => {
                  openModal({
                    component: InventoryUpdateModal,
                    props: { action: 'ADD', variantId },
                  });
                },
              },
              {
                label: t('shop.actions.remove_inventory'),
                disabled: !hasEditPermission(['internalMerch']),
                onClick: () => {
                  openModal({
                    component: InventoryUpdateModal,
                    props: { action: 'REMOVE', variantId },
                  });
                },
              },
            ]}
          />
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'inventory',
                label: t('shop.inventory.title'),
                url: ROUTES.shop.internal.inventory.details.replace(
                  ':id',
                  events?.meta.itemId ?? '',
                ),
                isActive: path === ROUTES.shop.internal.inventory.details,
                disabled: !events,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={2}>
              <Stack direction="row" alignItems="center" gap="15px">
                {isLoading ? (
                  <Skeleton variant="rounded" width={200} height={81} />
                ) : events?.meta.inventory !== undefined ? (
                  <Tile
                    title={t('shop.inventory.title')}
                    items={[String(events.meta.inventory)]}
                  />
                ) : null}
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="10px"
              >
                <ListTotal total={events?.total} />

                <Stack direction="row" alignItems="center" gap="10px">
                  {/* Filters */}

                  {/* Divider */}

                  <DownloadXlsAction
                    listLength={events?.total ?? 0}
                    exportToExcel={() => exportToXls({ variantId })}
                    loading={isExportingToXls}
                  />
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={events?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'createdAt',
                header: t('common.date'),
                cell: ({ row: { original: event } }) => {
                  return moment(event.createdAt.split('T')[0]).format('l');
                },
              },
              {
                id: 'inventoryChange',
                header: t('shop.inventoryChange.title'),
                cell: ({ row: { original: event } }) => {
                  return event.inventoryChange;
                },
                meta: { align: 'right' },
              },
              {
                id: 'doneBy',
                header: t('common.done_by'),
                cell: ({ row: { original: event } }) => {
                  return event.variant === 'monthlyCount' ? (
                    t('clients.feature-marketplace')
                  ) : event.doneBy ? (
                    <Link
                      label={event.doneBy.name}
                      to={ROUTES.administrators.details.replace(
                        ':id',
                        event.doneBy.id,
                      )}
                    />
                  ) : null;
                },
              },
              {
                id: 'comment',
                header: t('clients.comment'),
                cell: ({ row: { original: event } }) => {
                  return event.variant === 'monthlyCount'
                    ? t('orders.for_period', {
                        date: moment(event.createdAt.split('T')[0]).format(
                          'MMMM YYYY',
                        ),
                      })
                    : event.comment;
                },
              },
            ]}
            rows={{
              color: ({ original: event }) => {
                if (event.variant === 'monthlyCount') {
                  return SystemColors.grey[90];
                }
              },
            }}
            pagination={
              events && {
                count: events.total,
                page: events.page,
                pageSize: events.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
